import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Popover } from 'antd';

import { BellIcon } from '../../../assets';
import { fetchNotifications, fetchNotificationsForModal, markNotificationRead } from '../../../store/notifications';
import { AlertsPanel } from './AlertsPanel';
import { AlertsViewAllModal, VIEW_ALL_PAGE_SIZE } from './AlertsViewAllModal';
import { mapNotificationToAlert } from './mapNotification';
import { Navigation } from '../../../utils/navigation';
import styles from './index.module.css';

const formatBadgeCount = (count) => {
  if (count > 99) return '99+';
  return String(count);
};

export const NotificationBell = () => {
  const dispatch = useDispatch();
  const navigate = Navigation();
  const { items, unreadCount, modalItems, modalTotal, modalLoading } = useSelector(
    (state) => state.notifications
  );
  const [open, setOpen] = useState(false);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [viewAllPage, setViewAllPage] = useState(1);
  const [viewAllSortBy, setViewAllSortBy] = useState('newest');
  const [dismissedIds, setDismissedIds] = useState([]);
  const showBadge = unreadCount > 0;

  useEffect(() => {
    dispatch(fetchNotifications());
  }, [dispatch]);

  useEffect(() => {
    if (viewAllOpen) {
      dispatch(
        fetchNotificationsForModal({ page: viewAllPage, docsPerPage: VIEW_ALL_PAGE_SIZE, sortBy: viewAllSortBy })
      );
    }
  }, [dispatch, viewAllOpen, viewAllPage, viewAllSortBy]);

  const alerts = useMemo(
    () => items.filter((notification) => !dismissedIds.includes(notification.id)).map(mapNotificationToAlert),
    [items, dismissedIds]
  );

  const modalAlerts = useMemo(() => modalItems.map(mapNotificationToAlert), [modalItems]);

  const handleOpenChange = useCallback((nextOpen) => {
    setOpen(nextOpen);
  }, []);

  const handleDelete = useCallback((id) => {
    setDismissedIds((prev) => [...prev, id]);
  }, []);

  const handleOpenAlert = useCallback((alert) => {
    if (alert.unread) {
      dispatch(markNotificationRead(alert.id));
    }

    if (alert.entityType === 'RIDE' && alert.entityId) {
      setOpen(false);
      setViewAllOpen(false);
      navigate.push(`/rides/${alert.entityId}`);
    } else if (alert.entityType === 'DRIVER' && alert.entityId) {
      setOpen(false);
      setViewAllOpen(false);
      navigate.push(`/drivers/${alert.entityId}/edit`);
    }
  }, [dispatch, navigate]);

  const handleViewAll = useCallback(() => {
    setOpen(false);
    setViewAllPage(1);
    setViewAllSortBy('newest');
    setViewAllOpen(true);
  }, []);

  const handleCloseViewAll = useCallback(() => {
    setViewAllOpen(false);
  }, []);

  const handleViewAllSortByChange = useCallback((nextSortBy) => {
    setViewAllSortBy(nextSortBy);
    setViewAllPage(1);
  }, []);

  return (
    <>
      <Popover
        trigger="click"
        placement="bottomRight"
        open={open}
        onOpenChange={handleOpenChange}
        overlayClassName={styles.overlay}
        content={
          <AlertsPanel
            alerts={alerts.slice(0, 5)}
            onDelete={handleDelete}
            onOpen={handleOpenAlert}
            onViewAll={handleViewAll}
          />
        }
      >
        <button
          type="button"
          className={styles.bellButton}
          aria-label={showBadge ? `Alerts & Updates, ${unreadCount} unread` : 'Alerts & Updates'}
          aria-expanded={open}
        >
          <img src={BellIcon} alt="" className={styles.bellIcon} aria-hidden="true" />
          {showBadge && (
            <span className={styles.badge} aria-hidden="true">
              {formatBadgeCount(unreadCount)}
            </span>
          )}
        </button>
      </Popover>

      <AlertsViewAllModal
        visible={viewAllOpen}
        onClose={handleCloseViewAll}
        alerts={modalAlerts}
        totalCount={modalTotal}
        loading={modalLoading}
        page={viewAllPage}
        onPageChange={setViewAllPage}
        sortBy={viewAllSortBy}
        onSortByChange={handleViewAllSortByChange}
        onOpen={handleOpenAlert}
      />
    </>
  );
};
